/**
 * Households hooks — TanStack Query wrappers around `HouseholdsApi`.
 *
 * Used by T7 for the auth guard (`useCurrentHousehold()`) and by T8 settings
 * screens (users, invites, multi-household membership).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  CreateInviteRequest,
  Household,
  HouseholdBrandPreferenceValue,
  HouseholdBrandPreferencesResponse,
  HouseholdSystemProductPreferenceValue,
  HouseholdSystemProductPreferencesResponse,
  HouseholdUpdateRequest,
  HouseholdUsersResponse,
  JoinHouseholdRequest,
  SelectedStoresResponse,
  StorePreferencesResponse,
  SwitchHouseholdRequest,
  UpdateMemberFieldsRequest,
} from '../../types/household';
import { useApi } from './context';
import { keys } from './keys';
import { makeOptimisticCacheUpdate } from './optimistic';
import { broadcastPreferenceChange } from '../preferenceBroadcast';

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/**
 * Current household (the one the access token is scoped to). Used by the
 * auth-guarded routing layer to confirm the signed-in user has a household
 * membership the backend can serve.
 */
export function useCurrentHousehold() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.current,
    queryFn: () => api.households.fetchCurrent(),
  });
}

/**
 * List of every household the user belongs to. Used by the household-switcher
 * in Settings (T8) and by the invite-acceptance flow.
 */
export function useHouseholds() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.list,
    queryFn: () => api.households.list(),
  });
}

export function useHouseholdUsers() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.users,
    queryFn: () => api.households.fetchUsers(),
  });
}

export function useHouseholdInvites() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.invites,
    queryFn: () => api.households.fetchInvites(),
  });
}

export function useSelectedStores() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.selectedStores,
    queryFn: () => api.households.getSelectedStores(),
  });
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

function invalidateCurrent(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.households.current });
}

function invalidateList(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.households.list });
}

// BUG21 — the household update *request* is camelCase while the cached row is
// snake_case, and several per-tap controls (adults/kids steppers, eating-style /
// cooking-frequency pickers, the travel switch) ride this one hook. The
// optimistic updater must map every request key to its row key — never
// blind-spread the patch, or an unmapped key writes a field the component never
// reads and that control stays laggy. `adults` / `kids` / `diet` already share
// the same name; the rest are renamed below. Exported for the regression test
// that pins the mapping (`eatingStyle` → `eating_style`, …).
export function applyHouseholdUpdate(
  old: Household | undefined,
  patch: HouseholdUpdateRequest
): Household | undefined {
  if (!old) return old;
  const next: Household = { ...old };
  if (patch.name !== undefined) next.name = patch.name;
  if (patch.latitude !== undefined) next.latitude = patch.latitude;
  if (patch.longitude !== undefined) next.longitude = patch.longitude;
  if (patch.address !== undefined) next.address = patch.address;
  if (patch.city !== undefined) next.city = patch.city;
  if (patch.stateProvince !== undefined) next.state_province = patch.stateProvince;
  if (patch.country !== undefined) next.country = patch.country;
  if (patch.postalCode !== undefined) next.postal_code = patch.postalCode;
  if (patch.discoveryRadiusKm !== undefined)
    next.discovery_radius_km = patch.discoveryRadiusKm;
  if (patch.adults !== undefined) next.adults = patch.adults;
  if (patch.kids !== undefined) next.kids = patch.kids;
  if (patch.eatingStyle !== undefined) next.eating_style = patch.eatingStyle;
  if (patch.cookingFrequency !== undefined)
    next.cooking_frequency = patch.cookingFrequency;
  if (patch.travelFlag !== undefined) next.travel_flag = patch.travelFlag;
  if (patch.diet !== undefined) next.diet = patch.diet;
  return next;
}

export function useUpdateCurrentHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: HouseholdUpdateRequest) =>
      api.households.updateCurrent(input),
    // BUG21 — optimistic write so the per-tap controls reflect within ~1 frame.
    // `onSettled` still invalidates `households.current` to reconcile
    // server-computed fields; with the optimistic value in place that refetch
    // runs in the background and no longer gates the visible change.
    // BUG13 — the PATCH no longer writes to `storelocations` or
    // `store_preferences`; auto-discover surfaces `discoveredStores` for the
    // StoresCard checklist, persisted separately via `useAddStoresToSelection`.
    ...makeOptimisticCacheUpdate<Household, HouseholdUpdateRequest>({
      queryClient: qc,
      queryKey: keys.households.current,
      updater: applyHouseholdUpdate,
    }),
  });
}

export function useJoinHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: JoinHouseholdRequest) => api.households.join(input),
    onSuccess: () => {
      invalidateCurrent(qc);
      invalidateList(qc);
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

export function useSwitchHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: SwitchHouseholdRequest) => api.households.switch(input),
    onSuccess: () => {
      // Switching rotates the session — wipe everything, let the session
      // bootstrap re-hydrate with the new household scope.
      qc.clear();
    },
  });
}

export function useLeaveHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (householdId: string) => api.households.leave(householdId),
    onSuccess: () => {
      invalidateList(qc);
      invalidateCurrent(qc);
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

export function useRemoveHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { householdId: string; deleteMembers?: boolean }) => {
      const options =
        args.deleteMembers !== undefined
          ? { deleteMembers: args.deleteMembers }
          : undefined;
      return api.households.remove(args.householdId, options);
    },
    onSuccess: () => {
      invalidateList(qc);
      invalidateCurrent(qc);
      void qc.invalidateQueries({ queryKey: keys.auth.me });
    },
  });
}

/**
 * Delete the current household (manager only). Used by the Settings "Delete
 * household" action.
 *
 * Unlike `useRemoveHousehold` which surgically invalidates a few keys, this
 * hook invalidates ALL queries on success: every household-scoped read in the
 * cache (households, stores, products, lists, offers, memberships, …) is now
 * pointing at data that no longer exists server-side. The blast radius is
 * total, so the simplest correct move is to wipe the cache and let the
 * caller's post-delete flow (re-login → bootstrap) repopulate it.
 */
export function useDeleteCurrentHousehold() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { householdId: string; deleteMembers: boolean }) =>
      api.households.remove(args.householdId, {
        deleteMembers: args.deleteMembers,
      }),
    onSuccess: () => {
      void qc.invalidateQueries();
    },
  });
}

export function useRemoveHouseholdUser() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => api.households.removeUser(userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.households.users });
    },
  });
}

export function useCreateHouseholdInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateInviteRequest) => api.households.createInvite(input),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: keys.households.invites });
    },
  });
}

export function useCancelHouseholdInvite() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (inviteId: string) => api.households.cancelInvite(inviteId),
    onSuccess: () => {
      // Cancelling removes a row from the pending list — same query key as
      // useHouseholdInvites — so the InvitesCard refetches automatically.
      void qc.invalidateQueries({ queryKey: keys.households.invites });
    },
  });
}

export function useSetSelectedStores() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (storeIds: string[]) => api.households.setSelectedStores(storeIds),
    // BUG21 — the mutation var is the absolute next id array (a full replace,
    // not a delta). Write it straight into the selected-stores cache so the
    // StoresCard checkbox flips immediately; `onSettled` reconciles. The
    // caller (`handleToggleSelected`) builds `nextIds` from the freshest cache
    // at toggle-time so rapid selects on different stores compose.
    ...makeOptimisticCacheUpdate<SelectedStoresResponse, string[]>({
      queryClient: qc,
      queryKey: keys.households.selectedStores,
      updater: (_old, storeIds) => ({ storeIds }),
    }),
  });
}

// ---------------------------------------------------------------------------
// T12.2 — per-member age group / dietary exclusions. Manager-only
// on the backend; the write invalidates the member list so the new fields show
// on the member rows immediately.
// ---------------------------------------------------------------------------

export function useUpdateMemberFields() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { userId: string; input: UpdateMemberFieldsRequest }) =>
      api.households.updateMemberFields(args.userId, args.input),
    // BUG21 — the member input is camelCase (`ageGroup`) while the cached user
    // row is snake_case (`age_group`); map the matching member's fields in the
    // users list so the age choice / exclusion chips reflect
    // immediately. `onSettled` reconciles.
    ...makeOptimisticCacheUpdate<
      HouseholdUsersResponse,
      { userId: string; input: UpdateMemberFieldsRequest }
    >({
      queryClient: qc,
      queryKey: keys.households.users,
      updater: (old, { userId, input }) => {
        if (!old) return old;
        return {
          users: old.users.map((u) => {
            if (u.id !== userId) return u;
            const next = { ...u };
            if (input.ageGroup !== undefined) next.age_group = input.ageGroup;
            if (input.exclusions !== undefined) next.exclusions = input.exclusions;
            return next;
          }),
        };
      },
    }),
  });
}

// ---------------------------------------------------------------------------
// T12.2 — per-store category affinity. `GET /store-preferences` is the full
// preference entries (ids + favourite + order + declared categories); the PUT
// writes one store's category set. Soft signal for T12.5 — never a hard filter.
// ---------------------------------------------------------------------------

export function useStorePreferences() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.storePreferences,
    queryFn: () => api.households.getStorePreferences(),
  });
}

export function useSetStoreCategories() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { storeId: string; categories: string[] }) =>
      api.households.setStoreCategories(args.storeId, args.categories),
    // BUG21 — replace the matching store entry's `categories` so the per-store
    // category chips reflect immediately; `onSettled` reconciles. `storeId`
    // matches the entry's `storelocation_id`.
    ...makeOptimisticCacheUpdate<
      StorePreferencesResponse,
      { storeId: string; categories: string[] }
    >({
      queryClient: qc,
      queryKey: keys.households.storePreferences,
      updater: (old, { storeId, categories }) => {
        if (!old) return old;
        return {
          selected: old.selected.map((entry) =>
            entry.storelocation_id === storeId ? { ...entry, categories } : entry
          ),
        };
      },
    }),
  });
}

// ---------------------------------------------------------------------------
// T12.2 — household-wide brand like/avoid. Mirrors the per-product brand
// preference hooks one level up. A brand is `like` OR `avoid`, never both;
// `setHouseholdBrandPreference` flips the role in place. Every write
// invalidates the household brand-preferences query.
// ---------------------------------------------------------------------------

export function useHouseholdBrandPreferences() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.brandPreferences,
    queryFn: () => api.households.getBrandPreferences(),
  });
}

function invalidateBrandPreferences(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.households.brandPreferences });
  // T12.5.1 (G) — refresh other tabs' badge queries (this read is one of the
  // three badge inputs).
  broadcastPreferenceChange();
}

export function useLinkHouseholdBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { brandId: string; preference: HouseholdBrandPreferenceValue }) =>
      api.households.linkBrandPreference(args.brandId, args.preference),
    onSuccess: () => invalidateBrandPreferences(qc),
  });
}

export function useSetHouseholdBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { brandId: string; preference: HouseholdBrandPreferenceValue }) =>
      api.households.setBrandPreference(args.brandId, args.preference),
    // BUG21 — flip the matching brand's role in place (the felt in-place
    // toggle). Add-from-search (`useLinkHouseholdBrandPreference`) stays on the
    // invalidate path: an optimistic insert needs a display name the link input
    // doesn't carry, and the search affordance already covers the brief wait.
    ...makeOptimisticCacheUpdate<
      HouseholdBrandPreferencesResponse,
      { brandId: string; preference: HouseholdBrandPreferenceValue }
    >({
      queryClient: qc,
      queryKey: keys.households.brandPreferences,
      updater: (old, { brandId, preference }) => {
        if (!old) return old;
        return {
          preferences: old.preferences.map((p) =>
            p.brand_id === brandId ? { ...p, preference } : p
          ),
        };
      },
      // T12.5.1 (G) — refresh other tabs' badge queries on settle.
      afterSettle: broadcastPreferenceChange,
    }),
  });
}

export function useUnlinkHouseholdBrandPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (brandId: string) => api.households.unlinkBrandPreference(brandId),
    // BUG21 — remove the matching brand entry immediately; `onSettled`
    // reconciles.
    ...makeOptimisticCacheUpdate<HouseholdBrandPreferencesResponse, string>({
      queryClient: qc,
      queryKey: keys.households.brandPreferences,
      updater: (old, brandId) => {
        if (!old) return old;
        return {
          preferences: old.preferences.filter((p) => p.brand_id !== brandId),
        };
      },
      afterSettle: broadcastPreferenceChange,
    }),
  });
}

// ---------------------------------------------------------------------------
// T12.2 (step 8) — household-wide system-product like/avoid. The exact mirror of
// the household brand-preference hooks. A system product is `like` OR `avoid`,
// never both; `setHouseholdSystemProductPreference` flips the role in place.
// Every write invalidates the household system-product-preferences query.
// ---------------------------------------------------------------------------

export function useHouseholdSystemProductPreferences() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.systemProductPreferences,
    queryFn: () => api.households.getSystemProductPreferences(),
  });
}

function invalidateSystemProductPreferences(qc: ReturnType<typeof useQueryClient>) {
  void qc.invalidateQueries({ queryKey: keys.households.systemProductPreferences });
  // T12.5.1 (G) — refresh other tabs' badge queries (one of the badge inputs).
  broadcastPreferenceChange();
}

export function useLinkHouseholdSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { systemProductId: string; preference: HouseholdSystemProductPreferenceValue }) =>
      api.households.linkSystemProductPreference(args.systemProductId, args.preference),
    onSuccess: () => invalidateSystemProductPreferences(qc),
  });
}

export function useSetHouseholdSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { systemProductId: string; preference: HouseholdSystemProductPreferenceValue }) =>
      api.households.setSystemProductPreference(args.systemProductId, args.preference),
    // BUG21 — flip the matching product's role in place. Add-from-search
    // (`useLinkHouseholdSystemProductPreference`) stays on the invalidate path
    // (an optimistic insert needs the SystemProduct `name`, which the link
    // input doesn't carry).
    ...makeOptimisticCacheUpdate<
      HouseholdSystemProductPreferencesResponse,
      { systemProductId: string; preference: HouseholdSystemProductPreferenceValue }
    >({
      queryClient: qc,
      queryKey: keys.households.systemProductPreferences,
      updater: (old, { systemProductId, preference }) => {
        if (!old) return old;
        return {
          preferences: old.preferences.map((p) =>
            p.systemproduct_id === systemProductId ? { ...p, preference } : p
          ),
        };
      },
      afterSettle: broadcastPreferenceChange,
    }),
  });
}

export function useUnlinkHouseholdSystemProductPreference() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (systemProductId: string) =>
      api.households.unlinkSystemProductPreference(systemProductId),
    // BUG21 — remove the matching product entry immediately; `onSettled`
    // reconciles.
    ...makeOptimisticCacheUpdate<HouseholdSystemProductPreferencesResponse, string>({
      queryClient: qc,
      queryKey: keys.households.systemProductPreferences,
      updater: (old, systemProductId) => {
        if (!old) return old;
        return {
          preferences: old.preferences.filter(
            (p) => p.systemproduct_id !== systemProductId
          ),
        };
      },
      afterSettle: broadcastPreferenceChange,
    }),
  });
}

// ---------------------------------------------------------------------------
// T12.5.1 (C) — household catalog preference signals (read-only). The
// per-product brand + product preferences (preferred / substitute / exclude,
// carrying the value), rolled up to the household. The Deals/Planning surfaces
// render the full stacked badge set from these (display only, not ranking). The
// key is invalidated by every per-product preference mutation (G) so badges
// live-update without a reload.
// ---------------------------------------------------------------------------

export function useHouseholdCatalogPreferenceSignals() {
  const api = useApi();
  return useQuery({
    queryKey: keys.households.catalogPreferenceSignals,
    queryFn: () => api.households.getCatalogPreferenceSignals(),
  });
}
